// frontend/user/src/components/pack-add-files-page.tsx
// .pen UGuBc 页面-资源包-添加文件 — pack-create"添加文件"全屏选择页(2026-06-07 取代旧底部弹层)。
//
// 形态:全屏 overlay 组件(非路由)— pack-create 表单 state 不卸载(同 MoveFileDialog 模式)。
// 交互:
//   header  取消 | 已选 N 项 | 全选(N=选中行数,文件夹算 1 行,展开前)
//   面包屑  根"我的网盘",点任意层跳回
//   行      folder:点行下钻,点 checkbox 勾选整夹;file:点行/checkbox 均切换勾选
//   确认    "添加 (N)" — 文件夹 BFS 展开为文件(pack-file-expand),去重后整体替换 pack-create 选择
// 排序:sectionHeader 排序按钮(.pen MkGpG)→ ndI5h SortFilterSheet,客户端 sortFileRows(2026-06-11 接入)。
// 规格:docs/superpowers/specs/2026-06-07-pack-add-files-page-design.md §4-§5
import { useMemo, useState } from 'react'
import { ArrowUpDown, Check, ChevronRight, Plus } from 'lucide-react'
import { Spinner, cn, formatBytes, formatRelativeTime, useToast } from '@dogdisk/shared'
import { FileIcon } from './file-icon'
import { fetchFilePickPage, useFilePickList, type FileInfoOption } from '../_lib/pack-api'
import { ExpandError, expandSelection } from '../_lib/pack-file-expand'
import { SortFilterSheet } from './modals/sort-filter-sheet'
import { DEFAULT_SORT, sortFileRows, type SortState } from '../_lib/file-sort'

type Crumb = { id: string | null; name: string }

type Props = {
  /** pack-create 当前已选(进入页面时预填勾选) */
  initialSelected: FileInfoOption[]
  /** 取消:放弃本次选择 */
  onCancel: () => void
  /** 确认:展开后的纯文件数组,整体替换 pack-create 的 selected */
  onConfirm: (files: FileInfoOption[]) => void
}

export function PackAddFilesPage({ initialSelected, onCancel, onConfirm }: Props) {
  const toast = useToast()
  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: null, name: '我的网盘' }])
  const parentId = crumbs[crumbs.length - 1]?.id ?? null
  const { data, isLoading } = useFilePickList(parentId)
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [sortOpen, setSortOpen] = useState(false)
  const rows = useMemo(
    () =>
      sortFileRows(
        (data ?? []).map((f) => ({ ...f, created_at: f.created_at ?? '' })),
        sort,
      ),
    [data, sort],
  )
  const [selected, setSelected] = useState<ReadonlyMap<string, FileInfoOption>>(
    () => new Map(initialSelected.map((f) => [f.id, f])),
  )
  const [busy, setBusy] = useState(false)

  // 当前层是否已全选(全选按钮的文案/行为依据;跨层已选不参与判断)
  const allInLayer = useMemo(
    () => rows.length > 0 && rows.every((r) => selected.has(r.id)),
    [rows, selected],
  )

  const toggle = (f: FileInfoOption) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(f.id)) {
        next.delete(f.id)
      } else {
        next.set(f.id, f)
      }
      return next
    })
  }

  const toggleAll = () => {
    if (rows.length === 0) return
    setSelected((prev) => {
      const next = new Map(prev)
      if (allInLayer) {
        rows.forEach((r) => next.delete(r.id))
      } else {
        rows.forEach((r) => next.set(r.id, r))
      }
      return next
    })
  }

  const enterFolder = (f: FileInfoOption) => {
    setCrumbs((prev) => [...prev, { id: f.id, name: f.name }])
  }

  const onConfirmClick = async () => {
    setBusy(true)
    try {
      const files = await expandSelection(Array.from(selected.values()), fetchFilePickPage)
      if (files.length === 0) {
        toast.push('所选文件夹为空', { variant: 'error' })
        return
      }
      onConfirm(files)
    } catch (e) {
      const msg =
        e instanceof ExpandError ? e.message : e instanceof Error ? e.message : '添加失败,请重试'
      toast.push(msg, { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      data-testid="UGuBc"
      className="fixed inset-0 z-40 mx-auto flex w-full max-w-[402px] flex-col bg-white"
    >
      {/* .pen yac9B header:取消 | 已选 N 项 | 全选 + 面包屑 */}
      <header
        className="flex flex-col gap-3 px-5 pb-2"
        style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}
      >
        <div className="flex h-11 items-center justify-between">
          <button
            type="button"
            data-testid="pack-add-cancel-btn"
            onClick={onCancel}
            className="text-[16px] font-semibold text-[#19caad]"
          >
            取消
          </button>
          <span data-testid="pack-add-count" className="text-[17px] font-semibold text-[#1A1A1A]">
            已选 {selected.size} 项
          </span>
          <button
            type="button"
            data-testid="pack-add-select-all-btn"
            onClick={toggleAll}
            className="text-[16px] font-semibold text-[#19caad]"
          >
            {allInLayer ? '取消全选' : '全选'}
          </button>
        </div>
        <nav data-testid="pack-add-breadcrumb" className="flex items-center gap-1.5 overflow-x-auto">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1
            return (
              <span key={`${c.id ?? 'root'}-${i}`} className="flex shrink-0 items-center gap-1.5">
                {i > 0 && <ChevronRight size={12} className="text-[#D1D5DB]" aria-hidden="true" />}
                <button
                  type="button"
                  data-testid={`pack-add-crumb-${i}`}
                  disabled={last}
                  onClick={() => setCrumbs((prev) => prev.slice(0, i + 1))}
                  className={cn(
                    'text-[13px]',
                    last ? 'font-semibold text-[#1A1A1A]' : 'font-medium text-[#19caad]',
                  )}
                >
                  {c.name}
                </button>
              </span>
            )
          })}
        </nav>
      </header>

      {/* .pen Ub5cf fileSection */}
      <main className="flex-1 overflow-y-auto px-5 pb-6">
        <div className="flex items-center justify-between pt-2 pb-3">
          <div className="flex items-baseline gap-2">
            <h2 className="text-[17px] font-semibold text-[#1A1A1A]">
              {crumbs.length === 1 ? '所有文件' : '文件夹内容'}
            </h2>
            <span className="text-[13px] text-[#9CA3AF]">{rows.length} 项</span>
          </div>
          <button
            type="button"
            data-testid="pack-add-sort-btn"
            onClick={() => setSortOpen(true)}
            className="flex items-center gap-1.5 rounded-[14px] bg-[#F6F7F8] px-2.5 py-1.5 text-[12px] font-medium text-[#6B7280]"
          >
            <ArrowUpDown size={14} aria-hidden="true" />
            排序
          </button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner className="text-gray-400" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-[13px] text-[#9CA3AF]">此文件夹为空</p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {rows.map((f) => {
              const isFolder = f.type === 'folder'
              const checked = selected.has(f.id)
              const onRowClick = () => (isFolder ? enterFolder(f) : toggle(f))
              return (
                <li key={f.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    data-testid={`pack-add-row-${f.id}`}
                    onClick={onRowClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onRowClick()
                      }
                    }}
                    className={cn(
                      'flex h-[68px] w-full cursor-pointer items-center gap-3 rounded-[14px] px-3.5 text-left',
                      checked ? 'bg-[#E9F8F5] ring-[1.5px] ring-inset ring-[#19caad]' : 'bg-[#F6F7F8]',
                    )}
                  >
                    <button
                      type="button"
                      data-testid={`pack-add-checkbox-${f.id}`}
                      aria-label={checked ? `取消选择 ${f.name}` : `选择 ${f.name}`}
                      aria-pressed={checked}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggle(f)
                      }}
                      className={cn(
                        'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[11px]',
                        checked ? 'bg-[#19caad]' : 'border-[1.5px] border-[#D1D5DB] bg-white',
                      )}
                    >
                      {checked && <Check size={14} strokeWidth={3} className="text-white" />}
                    </button>
                    <FileIcon type={f.type} mimeType={f.mime_type} name={f.name} size={40} iconSize={18} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-semibold text-[#1A1A1A]">{f.name}</p>
                      <p className="text-[12px] text-[#9CA3AF]">
                        {isFolder
                          ? `文件夹${f.created_at ? ` · ${formatRelativeTime(f.created_at)}` : ''}`
                          : [f.size != null ? formatBytes(f.size) : null, f.created_at ? formatRelativeTime(f.created_at) : null]
                              .filter(Boolean)
                              .join(' · ')}
                      </p>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </main>

      {/* .pen LkWC3 confirmFooter */}
      <footer
        className="border-t border-[#F3F4F6] bg-white px-5 pt-4 shadow-[0_-2px_16px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          data-testid="pack-add-confirm-btn"
          disabled={selected.size === 0 || busy}
          onClick={() => void onConfirmClick()}
          className="flex h-[52px] w-full items-center justify-center gap-2 rounded-[26px] bg-[#19CAAD] text-[16px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
        >
          {busy ? <Spinner className="text-white" /> : <Plus size={20} aria-hidden="true" />}
          {busy ? '添加中…' : `添加 (${selected.size})`}
        </button>
      </footer>

      <SortFilterSheet
        open={sortOpen}
        onClose={() => setSortOpen(false)}
        value={sort}
        onChange={setSort}
      />
    </div>
  )
}
